import { Button, Card, cn } from '@vivicta/ui';
import { X } from 'lucide-react';
import { type ReactNode, useContext } from 'react';

import {
  type GitHubConnection,
  GitHubConnectionContext,
  useGitHubConnection,
} from './useGitHubConnection';
import { useSessionDismissal } from './useSessionDismissal';

const DISMISS_KEY_PREFIX = 'vivicta.githubBanner.';

type BannerProps = {
  /** What the user is seeing now and what connecting adds. */
  message: ReactNode;
  /**
   * Lets the user close the banner for this browser session. Leave it out
   * when connecting is the only thing the page offers.
   */
  dismissKey?: string;
  className?: string;
};

const BannerView = ({
  connection,
  message,
  dismissKey,
  className,
}: BannerProps & { connection: GitHubConnection }) => {
  const notice = useSessionDismissal(`${DISMISS_KEY_PREFIX}${dismissKey}`);

  if (connection.state !== 'none' || (dismissKey && notice.dismissed)) {
    return null;
  }

  return (
    <Card
      role="region"
      aria-label="GitHub connection"
      className={cn(
        'relative flex flex-col gap-3 p-4 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between',
        dismissKey && 'pr-12',
        className,
      )}
    >
      <span>
        {message}
        {connection.error && (
          <span className="mt-1 block text-destructive-strong">
            {connection.error}
          </span>
        )}
      </span>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="shrink-0 self-start sm:self-auto"
        disabled={connection.connecting}
        onClick={() => void connection.connect()}
      >
        {connection.connecting ? 'Connecting…' : 'Connect GitHub'}
      </Button>
      {dismissKey && (
        <Button
          type="button"
          size="icon-sm"
          variant="ghost"
          className="absolute right-1.5 top-1.5 size-7"
          aria-label="Dismiss for this session"
          onClick={notice.dismiss}
        >
          <X aria-hidden="true" />
        </Button>
      )}
    </Card>
  );
};

const OwnConnectionBanner = (props: BannerProps) => (
  <BannerView connection={useGitHubConnection()} {...props} />
);

/**
 * Non-blocking Connect prompt for a page that already shows what it can
 * without the user's GitHub token. Renders nothing once GitHub is connected.
 * Inside a `GitHubConnectionGate` preview it shares the gate's connection.
 */
export const GitHubConnectionBanner = (props: BannerProps) => {
  const gateConnection = useContext(GitHubConnectionContext);
  return gateConnection ? (
    <BannerView connection={gateConnection} {...props} />
  ) : (
    <OwnConnectionBanner {...props} />
  );
};
